import { action, makeObservable, observable } from 'mobx'
import qs from 'qs'
import type { ALL_WORKSHEET_TAG_LIST } from 'src/constant'

import { DTO } from '../DTO'
import type { Sort } from './ReqParams'

type PageParamsData<customParamsT extends Record<string, any>> = {
  name?: string
  title?: string
  customParams?: customParamsT
  tag?: (typeof ALL_WORKSHEET_TAG_LIST)[number]['value']
  isLast?: boolean
  sorts?: Sort[]
  page?: number
  size?: number
  totalPages?: number
}

// 페이징
export class PageParams<customParamsT extends Record<string, any> = {}> extends DTO {
  name?: string
  customParams: customParamsT
  isLast = false
  sorts?: Sort[]
  page = 0
  size = 30
  totalPages = 0

  constructor(data?: PageParamsData<customParamsT>) {
    super()

    this.customParams = data?.customParams ?? ({} as customParamsT)

    if (data) {
      this.name = data.name
      this.sorts = data.sorts

      if (data.page) this.page = data.page
      if (data.totalPages) this.totalPages = data.totalPages
      if (data.size) this.size = data.size
      if (data.isLast) this.isLast = data.isLast
    }

    makeObservable(this, {
      page: observable,
      size: observable,
      name: observable,
      customParams: observable,
      sorts: observable,
      isLast: observable,
      totalPages: observable,

      setSorts: action,
      setPage: action,
      setLastPage: action,
      setTotalPages: action,
      resetPage: action,
      updateCustomParams: action,
    })
  }

  private get properties() {
    return {
      customParams: this.customParams,
      name: this.name,
      page: this.page,
      size: this.size,
      sorts: this.sorts,
      isLast: this.isLast,
    }
  }

  copy(overwriteProperties: PageParamsData<customParamsT>) {
    return new PageParams(Object.assign({}, this.properties, overwriteProperties))
  }

  get toQueryString() {
    return PageParams.makePageParams<customParamsT>(this)
  }

  get toSizeQueryString() {
    return PageParams.makePageParams<customParamsT>(
      new PageParams<customParamsT>({
        name: this.name,
        customParams: this.customParams,
        sorts: this.sorts,
        page: 0,
        size: this.size + this.page * this.size,
      }),
    )
  }

  get toQueryStringFirstPage() {
    return PageParams.makePageParams<customParamsT>(this)
  }

  setPage(page: number) {
    this.page = page
  }

  nextPage() {
    this.page += 1
  }

  resetPage() {
    this.page = 0
  }

  setSize = (size: number) => {
    this.size = size
  }

  setLastPage = (isLast: boolean) => {
    this.isLast = isLast
  }

  setTotalPages = (totalPages: number) => {
    this.totalPages = totalPages
  }

  setSorts(sorts: PageParamsData<customParamsT>['sorts']) {
    this.sorts = sorts
  }

  updateCustomParams(params: Partial<this['customParams']>) {
    Object.assign(this.customParams, params)
  }

  /***
   * sort가 여러개가 들어갈 수 있으며 콤마(,) 쌍으로 들어가는 형식이라 제작
   * {{HOST}}:{{PORT}}/teachers?size=10&page=0&sort=id,ASC&sort=name,DESC
   * @example {
   *  makePageParams({ size: 10, page: 0, sort: [['id', 'ASC'], ['name', 'DESC]] })
   *  @return ?size=10&page=0&sort=id,ASC&sort=name,DESC
   * }
   ***/
  static makePageParams<paramsT extends Record<string, any>>(pageParams: PageParams<paramsT>) {
    const keys = Object.keys(pageParams) /*?*/
    if (keys.length === 0) return ''

    let stringify = ''
    const { size, page, sorts, name, customParams } = pageParams
    stringify += qs.stringify({ page, size, name, ...customParams }) /*?*/
    if (sorts) {
      if (stringify.length > 0) stringify += '&'
      stringify += sorts
        .map((sort) => qs.stringify({ sort }, { arrayFormat: 'comma', encode: false }))
        .join('&') /*?*/
    }

    return stringify.length > 0 ? '?' + stringify : ''
  }
}
